import type { LatLngExpression } from "leaflet";

import { convertImpacts } from "./impacts.js";
import type { CompanyDataResponse } from "./types/company.js";
import type {
	Geofence,
	PlantingDataResponse,
	RawCountryPlantingData,
	RawRegionPlantingData,
	RawSubSite,
} from "./types/planting.js";
import type {
	CleanedCompanyData,
	CleanedPlantingData,
	CountryPlantingData,
	RegionPlantingData,
	SubSite,
} from "./types/processed.js";

/**
 * Cleans the raw company data from the API.
 *
 * @param rawData The raw company data from the API.
 * @returns The cleaned company data.
 */
export function cleanCompanyData(
	rawData: CompanyDataResponse,
): CleanedCompanyData {
	const { data } = rawData;
	const impacts = convertImpacts(data.impacts, data.trees_by_forest_types);
	const treesByForestTypes = data.trees_by_forest_types.map((forestType) => ({
		designated: forestType.trees_designated,
		id: forestType.forest_type_id,
		name: forestType.type,
		ordered: forestType.trees_ordered,
		planted: forestType.trees_planted,
	}));
	const countries = data.countries;
	const trees = {
		designated: data.trees_designated,
		ordered: data.trees_ordered,
		planted: data.trees_planted,
	};
	return {
		countries,
		impacts,
		trees,
		treesByForestTypes,
	};
}

/**
 * Cleans the raw planting data from the API.
 *
 * @param rawData The raw planting data from the API.
 * @returns The cleaned planting data.
 */
export function cleanPlantingData(
	rawData: PlantingDataResponse,
): CleanedPlantingData {
	return rawData.data.map(cleanCountry);
}

/**
 * Gets the image for a subsite.
 *
 * @param subsite The subsite to get the image for.
 * @returns The image for the subsite or null if undefined.
 */
export function getSubSiteImage(
	subsite: Pick<RawSubSite, "subsite_main_image_url">,
): null | string {
	// An empty cdn_url (no image uploaded) should also fall back to null, not just a missing object.
	// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
	return subsite.subsite_main_image_url?.cdn_url || null;
}

/**
 * Cleans the raw country data.
 *
 * @param raw The raw country data.
 * @returns The cleaned country data.
 */
function cleanCountry(raw: RawCountryPlantingData): CountryPlantingData {
	const { id, lat, long, name, regions } = raw;
	return {
		id,
		lat,
		long,
		name,
		regions: regions.map(cleanRegion),
	};
}

/**
 * Cleans the raw region data.
 *
 * @param raw The raw region data.
 * @returns The cleaned region data.
 */
function cleanRegion(raw: RawRegionPlantingData): RegionPlantingData {
	const { center_lat, center_long, id, name, organization, subsites } = raw;
	return {
		id,
		lat: center_lat,
		long: center_long,
		name,
		organization,
		subSites: subsites.map(cleanSubSite),
	};
}

/**
 * Cleans the raw subsite data.
 *
 * @param raw The raw subsite data.
 * @returns The cleaned subsite data.
 */
function cleanSubSite(raw: RawSubSite): SubSite {
	const {
		description,
		forest_type_id,
		forest_type_name,
		geofence: rawGeofence,
		id,
		kelp_designated = 0,
		kelp_ordered = 0,
		kelp_planted = 0,
		name,
		site_manager_name,
		site_manager_title,
		subsite_area,
		total_planters,
		trees_designated = 0,
		trees_ordered = 0,
		trees_planted = 0,
		type,
	} = raw;

	const counts =
		type === "Trees"
			? {
					designated: trees_designated,
					ordered: trees_ordered,
					planted: trees_planted,
				}
			: {
					designated: kelp_designated,
					ordered: kelp_ordered,
					planted: kelp_planted,
				};
	const geofence = rawGeofence
		? {
				coordinates: convertCoordinates(rawGeofence.coordinates),
				type: rawGeofence.type,
			}
		: undefined;
	return {
		area: subsite_area,
		counts,
		description,
		forestType: {
			id: forest_type_id,
			name: forest_type_name,
		},
		geofence,
		id,
		image: getSubSiteImage(raw),
		manager: {
			name: site_manager_name,
			title: site_manager_title,
		},
		name,
		numberOfPlanters: total_planters,
		type,
	};
}

/**
 * Converts the raw coordinates to the format used by Leaflet.
 *
 * @param raw The raw coordinates.
 * @returns The converted coordinates.
 */
function convertCoordinates(
	raw: Geofence["coordinates"],
): LatLngExpression[][][] {
	return raw.map((polygon) =>
		polygon.map((ring) => ring.map(([lng, lat]) => [lat, lng])),
	);
}
